import clsx from 'clsx'

import { Icon, type IconProps } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'
import { fvDate } from '@fv/dates'
import { toggleArrayItem } from '@fv/models/core'

import CheckboxField from '@/components/inputs/CheckboxField'

import { HideForVendor } from '../auth/HasRole'
import { NewMessageIndicator } from './NewMessageIndicator'
import { ShipmentCardWorkflowLabel } from './ShipmentCardWorkflowLabel'
import type { SelectableShipmentProps } from './types'
import { VendorShipmentListIndicator } from './VendorShipmentIndicator'

const trackingIcons: { [key: string]: IconProps['icon'] } = {
  'picked-up': 'truck-moving',
  awarded: 'trophy',
  canceled: 'times',
  confirmed: 'clock',
  delivered: 'dolly',
  'out-for-delivery': 'dolly',
  bolOnly: 'file-contract',
  daily: 'calendar-day',
}

type ActiveShipmentCardHeaderProps = Partial<SelectableShipmentProps> & {
  carrierName?: string
  hideMessageIndicator?: boolean
  shipment: Pick<
    ListShipment,
    | 'loadId'
    | 'messageSummary'
    | 'pickup'
    | 'status'
    | 'tracking'
    | 'equipment'
    | 'locations'
    | 'workflow'
    | 'createdBy'
    | 'vendor'
  >
}

const ActiveShipmentCardHeader = ({
  carrierName,
  shipment,
  hideMessageIndicator,
  selected,
  setSelected,
}: ActiveShipmentCardHeaderProps) => {
  const { status, equipment, locations, pickup, workflow } = shipment
  const pickupStatus = pickup?.status
  const checked = selected?.includes(shipment.loadId) ?? false

  const getIcon = () => {
    if (pickupStatus === 'not-requested') {
      switch (workflow) {
        case 'parcel':
          return trackingIcons['daily']
        case 'ltl':
          return trackingIcons['bolOnly']
        default:
          return trackingIcons[status]
      }
    } else {
      return trackingIcons[status]
    }
  }

  return (
    <>
      <div className="flex items-center py-3 px-4 bg-white flex-1 overflow-hidden gap-x-3">
        {!!setSelected && (
          <HideForVendor>
            <CheckboxField
              name={`shipment-${shipment.loadId}`}
              className="flex-none"
              checked={checked}
              onChange={e => {
                setSelected(p =>
                  toggleArrayItem(p, shipment.loadId, e.target.checked),
                )
              }}
            />
          </HideForVendor>
        )}
        <ShipmentCardWorkflowLabel
          mode={equipment.mode}
          isMultiStop={locations.length > 2}
          carrierName={carrierName}
        />
      </div>
      {shipment.createdBy?.vendorId && (
        <VendorShipmentListIndicator name={shipment.vendor?.name} />
      )}
      {!hideMessageIndicator && (
        <NewMessageIndicator loadId={shipment.loadId} />
      )}
      <div
        className={clsx(
          'flex items-center justify-start z-20 border-l border-fv-gray gap-x-2 w-64 rounded-tr-lg px-4',

          {
            'bg-fv-beer-light':
              status === 'picked-up' || status === 'out-for-delivery',
            'bg-red-50': status === 'canceled',
            'bg-fv-green-100': status === 'delivered',
            'bg-fv-blue-50':
              status === 'confirmed' && pickupStatus !== 'not-requested',
            'bg-white':
              status === 'confirmed' && pickupStatus === 'not-requested',
          },
        )}
      >
        <div
          className={clsx(
            'w-4 h-4 rounded-full border-fv-gray border-2 -mt-[.115rem]',
            pickupStatus === 'not-requested' && '!border-fv-orange-300',
            status === 'delivered' && '!border-fv-green',
            status === 'confirmed' && '!border-fv-orange',
            status === 'picked-up' && '!border-fv-beer-dark',
            status === 'out-for-delivery' && '!border-fv-beer-dark',
            status === 'canceled' && '!border-fv-red',
          )}
        />
        <div>
          <Icon className="text-fv-gray-dark" icon={getIcon()} />
          <span>{composeLoadStatus(shipment)}</span>
        </div>
      </div>
    </>
  )
}

export const getTransitStatus = (
  pickupStatus?: string,
  workflow?: string,
): string => {
  if (workflow === 'parcel') {
    return pickupStatus === 'ok' ? 'Scheduled' : 'Daily pickup'
  } else {
    return pickupStatus === 'ok' ? 'Scheduled' : 'Print BOL only'
  }
}

const composeLoadStatus = (
  shipment: ActiveShipmentCardHeaderProps['shipment'],
) => {
  const { pickup, status, tracking, workflow } = shipment

  switch (status) {
    case 'awarded':
      return 'Awarded'
    case 'picked-up':
      return 'In transit'
    case 'out-for-delivery':
      return 'Out for delivery'
    case 'canceled':
      return 'Canceled'
    case 'confirmed':
      return getTransitStatus(pickup?.status, workflow)
    case 'delivered': {
      const deliveryDate = tracking?.deliveryDateActual
      return `Delivered${
        deliveryDate ? ` ${fvDate(deliveryDate).format('ddd MMM D')}` : ''
      }`
    }
    default:
      return 'Unknown'
  }
}

export default ActiveShipmentCardHeader
